import { FC, useEffect, useMemo, useState } from 'react';

import CelebrationBanner from './shared/CelebrationBanner';
import NumberedCallout from './shared/NumberedCallout';
import PromiseBadge from './shared/PromiseBadge';
import RaceBar from './shared/RaceBar';
import {
  capitaliseFirst,
  interpolateStepTemplate,
  pickByRaceState,
  toOrdinal
} from './template-utils';
import CtaButton from '@components/quiz/shared/CtaButton';
import StepHeader from '@components/quiz/shared/StepHeader';
import StickyCtaBar from '@components/quiz/shared/StickyCtaBar';
import { formatUplift } from '@components/quiz/shared/format-uplift';
import SquadPitch from '@components/manual-team-entry/squad-pitch';
import { useReadiness } from '@components/quiz/ReadinessProvider';
import { useSquadPortraits } from '@components/quiz/ReadinessProvider/utils/useSquadPortraits';
import { deriveVerdictPitchFlags } from '@components/quiz/ReadinessProvider/utils/pitch-flags';
import { mapRaceBarPositions } from '@components/quiz/ReadinessProvider/utils/race-bar';
import { resolveRaceConfig } from '@components/quiz/ReadinessProvider/utils/race-state';
import { fetchTransferReplacements } from '@lib/api/transfer-replacements';
import { buildSanityImageUrl } from '@lib/sanity/image';
import type { SanityReadinessRaceVerdictBlock } from '@lib/sanity/types';

type RaceVerdictBlockProps = SanityReadinessRaceVerdictBlock & {
  onNext: () => void;
};

const LEAGUE_TYPE_FALLBACKS: Record<string, string> = {
  work: 'your work league',
  mates: "your mates' league",
  money: 'your money league'
};

/**
 * Step 8 (post-GW1, spec 6.5): same structural skeleton as the pre-GW1
 * verdict with the race replacing the readiness gap and every copy
 * element driven by the three-state model (behind/level/ahead), plus
 * the lateStart treatment for the manual path — which never fabricates
 * points figures.
 */
const RaceVerdictBlock: FC<RaceVerdictBlockProps> = ({
  celebrationEmoji,
  celebrationHeadline,
  celebrationBody,
  eyebrowTemplate,
  eyebrowFallback,
  headlineBehind,
  headlineLevel,
  headlineAhead,
  headlineLateStart,
  positionLabelTemplate,
  titlePaceLabel,
  youPillBehindTemplate,
  youPillLevelTemplate,
  youPillAheadTemplate,
  youPillLateStartLabel,
  ghostPillBehindTemplate,
  ghostPillLevelTemplate,
  ghostPillAheadLabel,
  ghostPillLateStartLabel,
  captionBehindTemplate,
  captionLevelTemplate,
  captionAheadTemplate,
  captionLateStartTemplate,
  trajectoryBehindTemplate,
  trajectoryLevelTemplate,
  trajectoryAheadTemplate,
  trajectoryLateStartTemplate,
  healBehind,
  healLevel,
  healAhead,
  healLateStart,
  pitchHeading,
  sellNoteDifferTemplate,
  sellNoteAgreeTemplate,
  sellNoteNoSellTemplate,
  captainCalloutMatchTemplate,
  captainCalloutMismatchTemplate,
  captainCalloutSkippedTemplate,
  captainCalloutBehindAppendTemplate,
  transferCalloutSelectedTemplate,
  transferCalloutRollTemplate,
  transferCalloutUndecidedTemplate,
  leagueCalloutBehindTemplate,
  leagueCalloutLevelTemplate,
  leagueCalloutAheadTemplate,
  leagueCalloutLateStartTemplate,
  turnLine,
  turnLineAhead,
  promiseIcon,
  promiseHeadline,
  promiseTermsLine,
  ctaTemplate,
  ctaAheadTemplate,
  ctaGenericText,
  onNext
}) => {
  const { state } = useReadiness();
  const { pitchTeam } = useSquadPortraits(state.team);
  const verdict = state.raceVerdict;

  const userSell =
    state.transferChoice?.type === 'roll' ? null : state.selectedTransferOut;

  const pitchFlags = useMemo(
    () =>
      deriveVerdictPitchFlags({
        team: state.team,
        userCaptain: state.isCaptainSkipped ? null : state.selectedCaptain,
        userSell,
        aiCaptainId: state.aiCaptainId,
        aiSellPlayerId: state.aiSellPlayerId
      }),
    [
      state.team,
      state.isCaptainSkipped,
      state.selectedCaptain,
      userSell,
      state.aiCaptainId,
      state.aiSellPlayerId
    ]
  );

  // Rank-1 uplift (vs the out player) for the transfer callout — same
  // fallback behaviour as the pre-GW1 verdict when the user went straight to
  // roll.
  const [fallbackRank1Points, setFallbackRank1Points] = useState<number | null>(
    null
  );

  const rank1FromState = state.transferReplacements?.[0]?.pointsUplift ?? null;

  useEffect(() => {
    if (rank1FromState !== null || !pitchFlags.aiSell) return;

    let isCancelled = false;
    const squadPlayerIds = state.team.reduce<string[]>((ids, member) => {
      if (member.player) ids.push(member.player.id);
      return ids;
    }, []);

    fetchTransferReplacements(pitchFlags.aiSell.id, squadPlayerIds)
      .then((result) => {
        if (isCancelled || !result.locked) return;
        setFallbackRank1Points(result.locked.pointsUplift);
      })
      .catch(() => undefined);

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!verdict) return null;

  const raceState = verdict.state;
  const config = resolveRaceConfig(state.raceConfig);
  const positions = mapRaceBarPositions(
    {
      state: raceState,
      gap: verdict.gap,
      projectedGap: verdict.projectedGap
    },
    config
  );

  const rank1Points = rank1FromState ?? fallbackRank1Points;

  const leagueDisplayName =
    verdict.leagueName ??
    (state.stakes ? LEAGUE_TYPE_FALLBACKS[state.stakes] : null) ??
    'your mini-league';

  const templateValues: Record<string, string | number> = {
    gap: verdict.gap,
    projectedGap: verdict.projectedGap ?? '',
    projectionGameweek: config.projectionGameweek,
    leagueName: leagueDisplayName,
    position: verdict.userRank !== null ? toOrdinal(verdict.userRank) : '',
    size: verdict.leagueSize ?? '',
    captainName: state.selectedCaptain?.displayName ?? '',
    aiCaptainName:
      state.captainComparison?.aiCaptain.displayName ??
      pitchFlags.aiCaptain?.displayName ??
      '',
    pointsDelta:
      state.captainComparison?.pointsDelta != null
        ? Math.abs(state.captainComparison.pointsDelta).toFixed(1)
        : '…',
    rank1Points: rank1Points !== null ? formatUplift(rank1Points) : '…',
    selectedPoints:
      state.transferChoice?.type === 'replacement' &&
      state.transferReplacements?.[state.transferChoice.index]
        ? formatUplift(
            state.transferReplacements[state.transferChoice.index].pointsUplift
          )
        : '',
    selectedName:
      state.transferChoice?.type === 'replacement' &&
      state.transferReplacements?.[state.transferChoice.index]
        ? state.transferReplacements[state.transferChoice.index].player
            ?.displayName ?? ''
        : '',
    userSellName: userSell?.displayName ?? '',
    aiSellName: pitchFlags.aiSell?.displayName ?? ''
  };

  const interpolate = (template?: string) =>
    template ? interpolateStepTemplate(template, templateValues) : '';

  const eyebrow = verdict.leagueName
    ? interpolate(eyebrowTemplate)
    : eyebrowFallback ?? 'Your race to win your mini-league';

  const headline = pickByRaceState(raceState, {
    behind: headlineBehind ?? 'Your rivals are pulling away.',
    level: headlineLevel ?? "This league is anyone's. Win it now.",
    ahead: headlineAhead ?? "You're top. Don't be the one who throws it away.",
    lateStart: headlineLateStart ?? 'Starting late? Start sharp.'
  });

  // "You: P4 of 12" — only when real standings exist
  const positionLabel =
    verdict.userRank !== null && verdict.leagueSize !== null
      ? interpolateStepTemplate(
          positionLabelTemplate ?? 'You: P{{position}} of {{size}}',
          { ...templateValues, position: verdict.userRank }
        )
      : undefined;

  const youPillText = pickByRaceState(raceState, {
    behind: interpolate(youPillBehindTemplate ?? 'YOU · {{gap}} PTS BEHIND'),
    level: interpolate(youPillLevelTemplate ?? 'YOU · {{gap}} BEHIND'),
    ahead: interpolate(youPillAheadTemplate ?? 'YOU · {{gap}} CLEAR'),
    lateStart: youPillLateStartLabel ?? 'YOU · STARTING NOW'
  });

  const ghostPillText = pickByRaceState(raceState, {
    behind: interpolate(
      ghostPillBehindTemplate ??
        'GW{{projectionGameweek}} · {{projectedGap}} BEHIND'
    ),
    level: interpolate(
      ghostPillLevelTemplate ??
        'GW{{projectionGameweek}} · {{projectedGap}} OFF PACE'
    ),
    ahead: interpolate(
      ghostPillAheadLabel ?? 'GW{{projectionGameweek}} · LEAD GONE'
    ),
    lateStart: interpolate(
      ghostPillLateStartLabel ?? 'GW{{projectionGameweek}} · ADRIFT'
    )
  });

  const caption = pickByRaceState(raceState, {
    behind: interpolate(captionBehindTemplate),
    level: interpolate(captionLevelTemplate),
    ahead: interpolate(captionAheadTemplate),
    lateStart: interpolate(captionLateStartTemplate)
  });

  const trajectory = pickByRaceState(raceState, {
    behind: interpolate(trajectoryBehindTemplate),
    level: interpolate(trajectoryLevelTemplate),
    ahead: interpolate(trajectoryAheadTemplate),
    lateStart: interpolate(trajectoryLateStartTemplate)
  });

  const healLine = pickByRaceState(raceState, {
    behind: healBehind,
    level: healLevel,
    ahead: healAhead,
    lateStart: healLateStart
  });

  const sellNote =
    pitchFlags.sellNoteVariant === 'agree'
      ? interpolate(sellNoteAgreeTemplate)
      : pitchFlags.sellNoteVariant === 'differ'
      ? interpolate(sellNoteDifferTemplate)
      : interpolate(sellNoteNoSellTemplate);

  const captainOutcome = state.isCaptainSkipped
    ? 'skipped'
    : state.captainComparison
    ? state.captainComparison.isUserMatchingAi
      ? 'match'
      : 'mismatch'
    : 'skipped';

  const captainCalloutBase = interpolate(
    captainOutcome === 'match'
      ? captainCalloutMatchTemplate
      : captainOutcome === 'mismatch'
      ? captainCalloutMismatchTemplate
      : captainCalloutSkippedTemplate
  );

  // Behind state appends the gap-recovery line (spec 6.5)
  const captainCalloutText =
    raceState === 'behind' && captainCalloutBase
      ? [captainCalloutBase, interpolate(captainCalloutBehindAppendTemplate)]
          .filter(Boolean)
          .join(' ')
      : captainCalloutBase;

  const transferOutcome = !state.transferChoice
    ? 'undecided'
    : state.transferChoice.type === 'roll'
    ? 'roll'
    : 'selected';

  const transferCalloutText = interpolate(
    transferOutcome === 'selected'
      ? transferCalloutSelectedTemplate
      : transferOutcome === 'roll'
      ? transferCalloutRollTemplate
      : transferCalloutUndecidedTemplate
  );

  const leagueCalloutText = capitaliseFirst(
    pickByRaceState(raceState, {
      behind: interpolate(leagueCalloutBehindTemplate),
      level: interpolate(leagueCalloutLevelTemplate),
      ahead: interpolate(leagueCalloutAheadTemplate),
      lateStart: interpolate(leagueCalloutLateStartTemplate)
    })
  );

  const turnText = raceState === 'ahead' ? turnLineAhead ?? turnLine : turnLine;

  const ctaText =
    raceState === 'ahead' && verdict.leagueName
      ? interpolate(ctaAheadTemplate)
      : verdict.leagueName || state.stakes
      ? interpolate(ctaTemplate)
      : ctaGenericText ?? 'Unlock my plan to win my mini-league';

  return (
    <section className="flex flex-col items-center bg-neutral-100 px-4 pb-28 pt-8">
      <div className="w-full max-w-[600px]">
        {/* Celebration banner — ahead state only, celebrate then threat */}
        {raceState === 'ahead' && celebrationHeadline && (
          <div className="mb-6">
            <CelebrationBanner
              emoji={celebrationEmoji ?? '🏆'}
              headline={celebrationHeadline}
              body={celebrationBody}
            />
          </div>
        )}

        {/* Verdict block */}
        <StepHeader
          eyebrow={eyebrow}
          heading={headline}
          headingDataCy="readiness-race-verdict-headline"
        />

        {/* Race bar */}
        <div className="mt-6">
          <RaceBar
            pacePercent={positions.pacePercent}
            userPercent={positions.userPercent}
            ghostPercent={positions.ghostPercent}
            positionLabel={positionLabel}
            titlePaceLabel={titlePaceLabel ?? '🏁 Title pace'}
            youPillText={youPillText}
            ghostPillText={ghostPillText}
            caption={caption}
          />
        </div>

        {/* Trajectory — the projection never renders without its heal line */}
        {trajectory && healLine && (
          <div className="mt-8 rounded-xl bg-[#F8DDE2] px-5 py-4">
            <p className="font-heading text-base font-bold text-neutral-800">
              {trajectory}
            </p>
            <p className="mt-2 font-heading text-sm font-bold text-primary-500">
              {healLine}
            </p>
          </div>
        )}

        {/* Pitch heading */}
        {pitchHeading && (
          <p className="mb-3 mt-10 font-body text-xs font-bold uppercase tracking-widest text-neutral-500">
            {pitchHeading}
          </p>
        )}
      </div>

      {/* Pitch view — rendered outside the narrow content column so it sizes
          responsively (capped) like the captain/transfer screens and never
          crops players. */}
      <SquadPitch
        team={pitchTeam}
        flagsByPlayerId={pitchFlags.flagsByPlayerId}
      />

      <div className="w-full max-w-[600px]">
        {sellNote && (
          <p className="mt-3 font-body text-sm text-neutral-600">{sellNote}</p>
        )}

        {/* Numbered callouts */}
        <div className="mt-8 space-y-3">
          {captainCalloutText && (
            <NumberedCallout number={1} text={captainCalloutText} />
          )}
          {transferCalloutText && (
            <NumberedCallout number={2} text={transferCalloutText} />
          )}
          {leagueCalloutText && (
            <NumberedCallout number={3} text={leagueCalloutText} />
          )}
        </div>

        {/* The turn */}
        {turnText && (
          <p className="mt-8 text-center font-heading text-sm font-bold text-neutral-800">
            {turnText}
          </p>
        )}

        {/* Promise badge — the only screen carrying the promise */}
        {promiseHeadline && (
          <div className="mt-6">
            <PromiseBadge
              variant="light"
              iconUrl={
                promiseIcon
                  ? buildSanityImageUrl(promiseIcon).height(96).url()
                  : undefined
              }
              headline={promiseHeadline}
              termsLine={promiseTermsLine}
            />
          </div>
        )}
      </div>

      {/* Adaptive CTA — anchored to the bottom so it stays visible on the
          long verdict page. */}
      <StickyCtaBar maxWidthClass="max-w-[600px]">
        <CtaButton onClick={onNext} dataCy="readiness-race-verdict-cta">
          {ctaText}
        </CtaButton>
      </StickyCtaBar>
    </section>
  );
};

export default RaceVerdictBlock;
